import { useCallback, useId, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Center,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  InputSelectField,
  Typography,
} from '@tempmee/react';
import { OutlineCheckCircleConfirmationIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import {
  CancellationReasonJobType,
  isFilledJobCancellation,
  useGetCancellationReasons,
} from '~/domain/JobCancellation';
import { useGetJob } from '~/domain/Jobs';
import { routes } from '~/routes';
import { useCancellationSurveyForm } from './hooks/useCancellationSurveyForm';
import { useSubmitCancellationSurvey } from './hooks/useSubmitCancellationSurvey';
import { OtherReasonField } from './OtherReasonField';
import { ReasonsLoadError, ReasonsLoadErrorActions } from './ReasonsLoadError';

/** Figma's 358px panel sits between the DS sizes (250 / 620); same override as the Referral and BiD dialogs. */
const PANEL_STYLE = { maxWidth: 358 } as const;
const ILLUSTRATION_SIZE = 80;

// The survey is mandatory, so the Dialog's close request is ignored (it can only come from Esc/scrim, both off).
const ignoreClose = () => {};

/** GOTU-15983 survey. No `DialogTitle`: it would draw a close button from `onClose`, and the survey can't be skipped. */
export const CancellationSurveyDialog = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();
  const titleId = useId();
  const otherReasonId = useId();

  const jobQuery = useGetJob(jobId);
  const job = jobQuery.data;
  const reasonsQuery = useGetCancellationReasons(
    {
      jobType: job?.isOnsiteInterview
        ? CancellationReasonJobType.Interview
        : CancellationReasonJobType.Shift,
      filled: isFilledJobCancellation(job),
    },
    { enabled: Boolean(job) },
  );
  const reasons = reasonsQuery.data;

  const form = useCancellationSurveyForm(reasons);
  const { submit, isSubmitting, hasSubmitError } = useSubmitCancellationSurvey({ jobId, job });

  const isLoading = jobQuery.isLoading || reasonsQuery.isLoading;
  // Covers a failed load and a 200 with no job; a failed background refetch keeps the cached job.
  const hasNoJob = !jobQuery.isLoading && !job;
  // An empty list gets the way out too; a failed refetch that kept the list keeps the form.
  const hasNoReasons =
    Boolean(job) && reasons.length === 0 && (reasonsQuery.isError || !reasonsQuery.isLoading);
  const hasLoadError = hasNoJob || hasNoReasons;

  const options = useMemo(
    () => reasons.map((reason) => ({ label: reason.name, value: reason.code })),
    [reasons],
  );

  const { refetch: refetchJob } = jobQuery;
  const { refetch: refetchReasons } = reasonsQuery;
  const onRetry = useCallback(() => {
    if (hasNoJob) {
      refetchJob();
      return;
    }
    refetchReasons();
  }, [hasNoJob, refetchJob, refetchReasons]);

  const onExit = useCallback(() => navigate(routes.dashboardHome), [navigate]);

  const { answer } = form;
  const onSubmit = useCallback(() => {
    if (answer) submit(answer);
  }, [answer, submit]);

  return (
    <Dialog
      open
      onClose={ignoreClose}
      closeOnOverlayClick={false}
      closeOnEscape={false}
      size="medium"
      style={PANEL_STYLE}
      aria-labelledby={titleId}
      aria-busy={isLoading}
      data-cy="cancellation-survey-dialog"
    >
      <Center py={4}>
        <OutlineCheckCircleConfirmationIcon
          size={ILLUSTRATION_SIZE}
          color={colors.success[400]}
          aria-hidden
          data-cy="cancellation-survey-illustration"
        />
      </Center>
      <DialogContent>
        <Column gap={4}>
          <Typography id={titleId} variant="h6" textAlign="center" color={colors.text.primary}>
            Cancellation Successful
          </Typography>
          {hasLoadError ? (
            <ReasonsLoadError />
          ) : (
            <>
              <Box data-cy="cancellation-reason-select">
                <InputSelectField
                  label="Why do you need to cancel?"
                  options={options}
                  value={form.selectedCode}
                  isLoading={isLoading}
                  onChange={form.selectReason}
                />
              </Box>
              {form.isOther && (
                <OtherReasonField
                  id={otherReasonId}
                  value={form.otherText}
                  error={form.otherTextError}
                  onChange={form.changeOtherText}
                  onBlur={form.touchOtherText}
                />
              )}
              {hasSubmitError && (
                <Alert
                  severity="error"
                  description="We couldn't send your answer. Please try again."
                  data-cy="cancellation-submit-error"
                  action={
                    <Button
                      variant="text"
                      size="small"
                      data-cy="cancellation-submit-retry"
                      onClick={onSubmit}
                    >
                      Retry
                    </Button>
                  }
                />
              )}
            </>
          )}
        </Column>
      </DialogContent>
      <DialogActions>
        {hasLoadError ? (
          <ReasonsLoadErrorActions
            isRetrying={jobQuery.isFetching || reasonsQuery.isLoading}
            onRetry={onRetry}
            onExit={onExit}
          />
        ) : (
          <Button
            size="small"
            data-cy="submit-cancellation-feedback"
            disabled={!form.canSubmit || isLoading || isSubmitting}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
